import { addDays, endOfDay, isAfter, isBefore } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import { overdueSectionID } from 'shared/types/orderable-section';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { formatDateKey } from 'shared/utils/format-date-key';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
};

const noDeadlineSectionID = 'no-deadline';

export const useDeadlineTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const overdueDeadline = endOfDay(addDays(today, -1));
    const dateSections: Record<string, { date: Timestamp; taskIds: ID[] }> = {};
    const overdueTaskIds: ID[] = [];
    const noDeadlineTaskIds: ID[] = [];

    // Process each task to determine its deadline sections
    tasks.forEach((task) => {
      if (task.completedAt) {
        return;
      }

      // Check if task is overdue using same logic as use-due-status-grouped-tasks
      const overduePlanning = getDueTaskPlanning(task, {
        date: overdueDeadline,
        today,
        weekStartsOn,
      });

      const duePlanning = overduePlanning.filter(
        (plannedOccurrence) =>
          !isAfter(plannedOccurrence.date, overdueDeadline),
      );

      const plannedOccurrence = duePlanning[duePlanning.length - 1];
      const isOverdue =
        !!plannedOccurrence ||
        (task.deadlineDeadline &&
          !isAfter(task.deadlineDeadline, overdueDeadline));

      // Get all planned occurrences for grouping (using today as reference)
      const planning = getDueTaskPlanning(task, {
        date: today,
        today,
        weekStartsOn,
      });

      // Check for deadlineDeadline
      const hasDeadlineDeadline = !!task.deadlineDeadline;

      if (
        !planning.length &&
        !hasDeadlineDeadline &&
        !task.endStrategy?.deadline
      ) {
        // Task has no deadline
        noDeadlineTaskIds.push(task.id);
        return;
      }

      // If task is overdue, add to overdue section
      if (isOverdue) {
        overdueTaskIds.push(task.id);
      }

      // Collect all dates this task should appear in (for future/current dates)
      const taskDates = new Set<string>();

      // Add dates from planned occurrences (for scheduled tasks)
      // Only add dates that are today or in the future (not overdue)
      planning.forEach(({ date }) => {
        if (!isBefore(date, today)) {
          const dateKey = formatDateKey(date);
          taskDates.add(dateKey);
        }
      });

      // Add deadlineDeadline if it exists and is today or future
      if (hasDeadlineDeadline && !isBefore(task.deadlineDeadline!, today)) {
        const dateKey = formatDateKey(task.deadlineDeadline!);
        taskDates.add(dateKey);
      }

      // Add endStrategy.deadline if it exists and no schedules and is today or future
      if (
        !task.schedules?.length &&
        task.endStrategy?.deadline &&
        !isBefore(task.endStrategy.deadline, today)
      ) {
        const dateKey = formatDateKey(task.endStrategy.deadline);
        taskDates.add(dateKey);
      }

      // Add task to appropriate date sections (for future/current dates)
      taskDates.forEach((dateKey) => {
        if (!dateSections[dateKey]) {
          dateSections[dateKey] = {
            date: new Date(dateKey),
            taskIds: [],
          };
        }
        dateSections[dateKey].taskIds.push(task.id);
      });
    });

    // Build tree items for date sections
    const dateTreeItems: Record<ID, TreeItem> = {};

    // Add overdue section if there are overdue tasks
    if (overdueTaskIds.length) {
      dateTreeItems[overdueSectionID] = {
        id: overdueSectionID,
        type: TreeItemTypes.Date,
        name: t('general.sections.overdue.title'),
        children: [],
        isFolder: true,
      };
    }

    // Sort date sections chronologically and create tree items
    const sortedDateKeys = Object.keys(dateSections).sort(
      (a, b) => dateSections[a].date.getTime() - dateSections[b].date.getTime(),
    );

    sortedDateKeys.forEach((dateKey) => {
      dateTreeItems[dateKey] = {
        id: dateKey,
        type: TreeItemTypes.Date,
        name: dateKey, // Will be formatted in header component
        children: [],
        isFolder: true,
      };
    });

    // Add "No deadline" section if there are tasks without deadlines
    if (noDeadlineTaskIds.length) {
      dateTreeItems[noDeadlineSectionID] = {
        id: noDeadlineSectionID,
        type: TreeItemTypes.Date,
        name: t('general.labels.no-deadline'),
        children: [],
        isFolder: true,
      };
    }

    // Get custom order for all sections
    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? Object.keys(dateTreeItems).reduce<ID[]>((acc, sectionId) => {
            const sectionOrder = customOrder?.[sectionId] ?? [];
            acc.push(...sectionOrder);
            return acc;
          }, [])
        : [];

    // Sort tasks and assign to sections
    const allTreeItems = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.CreatedAt, sortingMode] : undefined,
    }).reduce<Record<ID, TreeItem>>((acc, task) => {
      // Determine which sections this task belongs to
      // Tasks can appear in multiple sections (overdue + future dates for scheduled tasks)
      if (overdueTaskIds.includes(task.id)) {
        if (acc[overdueSectionID]) {
          acc[overdueSectionID].children?.push(task.id);
        }
      }

      if (noDeadlineTaskIds.includes(task.id)) {
        if (acc[noDeadlineSectionID]) {
          acc[noDeadlineSectionID].children?.push(task.id);
        }
      } else {
        // Find all date sections this task belongs to
        Object.keys(dateSections).forEach((dateKey) => {
          if (dateSections[dateKey].taskIds.includes(task.id)) {
            if (acc[dateKey]) {
              acc[dateKey].children?.push(task.id);
            }
          }
        });
      }

      // Add task tree item
      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };

      return acc;
    }, dateTreeItems);

    // Build section IDs in order: overdue, date sections (chronological), no deadline
    const sectionIds: ID[] = [];
    if (overdueTaskIds.length) {
      sectionIds.push(overdueSectionID);
    }
    sectionIds.push(...sortedDateKeys);
    if (noDeadlineTaskIds.length) {
      sectionIds.push(noDeadlineSectionID);
    }

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: sectionIds,
      },
      ...allTreeItems,
    };
  }, [customOrder, sortingMode, t, tasks, today, weekStartsOn]);
};
